import {MutableRefObject, useState} from 'react'
import {ITableProps} from "@ui/tableCustom/types/table";
import {useExportChange} from "@containers/export/containers/buildData/hooks/useExportChange";
import ActionUiHandler from "@ui/template/actionUiHandler/ActionUiHandler";
import {SxType} from "@baseTypes/sx/sxTypes";
import TableCustomRootSideBar from "@ui/tableCustom/view/rootSideBar/TableCustomRootSideBar";
import PaginationCustom from "@ui/paginationCustom/PaginationCustom";
import TableCustomScreens from "@ui/tableCustom/view/screens/TableCustomScreens";
import {useTranslation} from 'react-i18next';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {SetStatePropsType} from "@baseTypes/base";
import {sx} from './sx'

interface IProps<T> extends ITableProps<T> {
    innerRef?: MutableRefObject<any>,
    maxHeightScreen?: string,
    fullScreenMode?: boolean,
    setFullScreenMode?: SetStatePropsType<boolean>
}

function BaseTableFeatures<T>({
                                  isLoading,
                                  isError,
                                  hasExportDataFromAllPage,
                                  mapperRowCb,
                                  data,
                                  allData,
                                  fullColumns,
                                  headColumns,
                                  params,
                                  headSideBar,
                                  secondarySidebar,
                                  hasExport = true,
                                  exportParams,
                                  mapperColumnCb,
                                  baseExportStyles,
                                  generateExportExcelRowStylesCb,
                                  generateExportExcelHeadColumnStylesCb,
                                  headerExportContents,
                                  initialStickyHeader,
                                  changeExportSidebarSettings,
                                  hasPdfExport = true,
                                  sx: sxProps = {},
                                  lastTableRow,
                                  menuSettingsTable,
                                  innerRef,
                                  maxHeightScreen,
                                  fullScreenMode,
                                  setFullScreenMode,
                                  hasModalFullScreen,
                                  totalPage,
                                  paginationProps,
                              }: IProps<T>) {

    const [stickyHeader, setStickyHeader] = useState(typeof initialStickyHeader !== 'undefined' ? initialStickyHeader : true)

    const {t} = useTranslation()

    const {page,} = params;

    const exportMutate = useExportChange({
        fullColumns,
        exportParams,
        enabled: hasExport,
        data
    })

    return (
        <ActionUiHandler
            isError={isError}
            isLoading={isLoading}
        >
            <Stack
                sx={{
                    ...sx.root,
                    ...sxProps
                } as SxType}
            >
                <Stack
                    direction={exportParams.exportMode ? 'column' : 'row'}
                    flexWrap={'wrap'}
                    justifyContent={exportParams.exportMode ? 'flex-start' : 'flex-end'}
                    alignItems={exportParams.exportMode ? 'flex-start' : 'center'}
                    ref={innerRef}
                    sx={sx.wrap}
                >
                    {!exportParams.exportMode && headSideBar && headSideBar}

                    <TableCustomRootSideBar
                        <T>
                        headColumns={headColumns}
                        fullColumns={fullColumns}
                        params={params}
                        hasExport={hasExport}
                        exportParams={exportParams}
                        exportMutate={exportMutate as any}
                        disabled={isLoading}
                        sidebar={secondarySidebar}
                        baseExportStyles={baseExportStyles}
                        allData={allData}
                        hasExportDataFromAllPage={hasExportDataFromAllPage}
                        headerExportContents={headerExportContents}
                        fullScreenMode={fullScreenMode} setFullScreenMode={setFullScreenMode}
                        hasModalFullScreen={hasModalFullScreen}
                        stickyHeader={stickyHeader} setStickyHeader={setStickyHeader}
                        generateExportExcelRowStylesCb={generateExportExcelRowStylesCb}
                        generateExportExcelHeadColumnStylesCb={generateExportExcelHeadColumnStylesCb}
                        changeExportSidebarSettings={changeExportSidebarSettings}
                        hasPdfExport={hasPdfExport}
                        menuSettingsTable={menuSettingsTable}
                    />

                    {!isLoading && paginationProps?.paginationPosition === 'top' && (totalPage || paginationProps?.emptyPagination) &&
                        <PaginationCustom
                            empty={paginationProps?.emptyPagination}
                            totalPage={totalPage}
                            page={page}
                            onChange={params.setPage}
                            boundaryCount={paginationProps?.boundaryCount}
                            size={paginationProps?.size}
                        />}
                </Stack>

                <Stack
                    sx={sx.inner}
                >
                    {data?.length >= 1 ? <TableCustomScreens
                        <T>
                        data={data}
                        fullColumns={fullColumns}
                        headColumns={headColumns}
                        params={params}
                        exportParams={exportParams}
                        exportMutate={exportMutate as any}
                        mapperRowCb={mapperRowCb}
                        mapperColumnCb={mapperColumnCb}
                        stickyHeader={stickyHeader}
                        fullScreenMode={fullScreenMode}
                        maxHeightScreen={maxHeightScreen}
                        lastTableRow={lastTableRow}

                    /> : !isLoading &&
                        <Typography variant={'body2'}>
                            {t('notFound')}
                        </Typography>
                    }

                    {!isLoading && (!paginationProps?.paginationPosition || paginationProps?.paginationPosition === 'bottom') && (totalPage || paginationProps?.emptyPagination) &&
                        <PaginationCustom
                            totalPage={totalPage}
                            page={page}
                            onChange={params.setPage}
                            empty={paginationProps?.emptyPagination}
                            boundaryCount={paginationProps?.boundaryCount ?? 1}
                            sx={paginationProps?.sxPagination}
                            size={paginationProps?.size}
                        />}
                </Stack>
            </Stack>
        </ActionUiHandler>
    )
}


export default BaseTableFeatures